import { useEffect } from 'react'
import { Link, NavLink, Outlet } from 'react-router-dom'
import { useMqttConnected, useMqttToasts, useMqttTopic } from '../mqtt.jsx'
import { fmtTime } from '../format.js'

const TABS = [
  { to: '/', label: 'Results', end: true },
  { to: '/all-runs', label: 'All Runs' },
  { to: '/compare', label: 'Compare' },
  { to: '/topten', label: 'Top Ten' },
]

// NIGAN's own richer display (72degrees/NIGAN_MQTT's web/, served under
// /web/ - see the SWAG conf) shares this preference with this app via one
// cookie: each NIGAN page writes its own key on load, and ?default=<key>
// here does the same, so whichever a viewer last visited (either app)
// becomes where a bare visit to `/` lands next time. Also drives the
// top-level nav's board picker below - "NHCA Live" (this app, as a whole)
// vs. each of NIGAN's own pages, one level above this app's own Results/
// All Runs/Compare/Top Ten sub-menu (which stays exactly where/how it
// always was - this top bar is a *board* picker, not a page picker).
const NIGAN_NAV = [
  { key: 'live', label: 'Live', file: 'live.html' },
  { key: 'results', label: 'Results', file: 'index.html' },
  { key: 'leaderboard', label: 'Leaderboard', file: 'leaderboard.html' },
  { key: 'topten', label: 'Top Ten', file: 'topten.html' },
  { key: 'monitor', label: 'Monitor', file: 'monitor.html' },
  // display.html now gets nav too (NIGAN_MQTT PR #5) - safe to include
  // here as a normal target since it no longer strands a viewer with no
  // way back.
  { key: 'display', label: 'Display', file: 'display.html' },
]
const NIGAN_PAGES = Object.fromEntries(NIGAN_NAV.map((p) => [p.key, p.file]))
const DEFAULT_COOKIE = 'nhca_live_default'

function readDefaultCookie() {
  const match = document.cookie.match(/(?:^|; )nhca_live_default=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : null
}

function writeDefaultCookie(value) {
  document.cookie = `${DEFAULT_COOKIE}=${value}; path=/; max-age=31536000`
}

/**
 * ?default=<key> (explicit, wins over the cookie) or the remembered cookie
 * picks which app a bare visit to `/` lands on - a recognized NIGAN page
 * key redirects to /web/<page>.html, anything else (including no param/
 * cookie at all) falls through to this app's own Results page. Computed
 * directly during render, not an effect, so a redirect never flashes this
 * app's UI first - matches this codebase's existing derive-in-render
 * preference over useEffect+setState (see ResultsPage/ComparePage's own
 * session-picking logic).
 */
function useNiganDefaultRedirect() {
  const params = new URLSearchParams(window.location.search)
  const paramValue = params.get('default')
  const requested = paramValue || readDefaultCookie()

  if (paramValue) writeDefaultCookie(paramValue)

  if (requested && NIGAN_PAGES[requested]) {
    window.location.replace(`/web/${NIGAN_PAGES[requested]}`)
    return true
  }
  return false
}

function Toasts() {
  const toasts = useMqttToasts()
  if (toasts.length === 0) return null
  return (
    <div className="toast-stack">
      {toasts.map((t) => (
        <div className="toast" key={t._id}>
          {t.event === 'flagged'
            ? `⚑ #${t.num} ${t.rider} — flagged (${t.session})`
            : `✓ #${t.num} ${t.rider} — ${fmtTime(t.time)}s (${t.session})`}
        </div>
      ))}
    </div>
  )
}

export default function Layout() {
  const redirecting = useNiganDefaultRedirect()
  const connected = useMqttConnected()
  const venue = useMqttTopic('venue')
  const ftd = useMqttTopic('ftd')

  useEffect(() => {
    if (!redirecting) writeDefaultCookie('board')
  }, [redirecting])

  if (redirecting) return null

  return (
    <div>
      <Toasts />
      <nav className="top-nav">
        <Link to="/" className="active">NHCA Live</Link>
        <span className="top-nav-divider" aria-hidden="true" />
        {NIGAN_NAV.map((p) => (
          <a key={p.key} href={`/web/${p.file}`}>
            {p.label}
          </a>
        ))}
      </nav>
      <div className="venue-band">
        <div className="venue-top">
          <div className="wordmark">
            <span className="flag">🏁</span>
            <b>NHCA Live</b>
            <small>National Hill Climb Association</small>
          </div>
          <div className={`live-pill ${connected ? '' : 'offline'}`}>
            <span className={`live-dot ${connected ? '' : 'offline'}`} />
            {connected ? 'Live' : 'Connecting…'}
          </div>
        </div>
        <h1 className="venue-title">{venue?.venue || 'Waiting for event…'}</h1>
        <div className="venue-sub">
          {venue?.date && <span>{venue.date}</span>}
          {venue?.entryCount != null && (
            <span>
              <b>{venue.entryCount}</b> entries
            </span>
          )}
        </div>
        {ftd && (
          <div className="ftd-strip">
            <div>
              <div className="eyebrow">Fastest time of day</div>
              <div className="ftd-name">
                #{ftd.num} {ftd.rider}
              </div>
            </div>
            <div className="ftd-time mono">{fmtTime(ftd.time)}</div>
          </div>
        )}
      </div>

      <nav className="tabbar">
        {TABS.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => (isActive ? 'active' : '')}>
            {t.label}
          </NavLink>
        ))}
      </nav>

      <main>
        <Outlet />
      </main>

      <p style={{ textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.72rem', padding: '10px 20px 26px' }}>
        Public live board · mqtt.nhca.co.uk ·{' '}
        <Link to="/admin" style={{ color: 'var(--text-dim)', textDecoration: 'underline' }}>Event admin →</Link>
      </p>
    </div>
  )
}
